// 파이널 모의고사 회차 정의 (JCG-2897). /final-mock-exam 회차 토글의 단일 진실 원천.
//
// 문제는 id 파생 finalSession("2026-1"·"2026-2" …)으로 회차에 매핑되고
// (scripts/sync-problems-to-firebase.mjs), 회차별 문제 수는 build-problems-index 가
// final-session-counts.json 으로 emit 한다(0개 회차는 키 없음).
//
// FINAL_SESSIONS 순서는 오래된 → 최신(위 → 아래). 기본 노출은 "문제가 있는 가장 최신 회차".
import finalSessionCounts from "./final-session-counts.json";

/** 파이널 모의고사 출제 분포 항목: subject(문제 파일)명 + 뽑을 문제 수. */
export interface FinalSessionScope {
  /** 문제 subject(파일)명 — 예: "c", "python", "network-os" */
  scope: string;
  /** 이 subject 에서 뽑을 문제 수 */
  count: number;
}

export interface FinalSession {
  /** finalSession 값 = 문제 id 파생 회차 토큰과 동일 (예: "2026-1") */
  id: string;
  /** 토글 버튼·안내 문구 공용 라벨 (예: "26년 1회 대비") */
  label: string;
  /**
   * 아직 정식 오픈 전이라, 문제를 미리 넣어 인덱스에 잡혀 있더라도 "준비 중"으로만 노출할 회차.
   * (예: 26년 2회 파이널을 제작하며 문제를 미리 추가하지만 공개는 오픈일까지 막을 때)
   * 정식 오픈 시 이 플래그만 제거하면 문제 수 기반 노출로 전환된다.
   */
  comingSoon?: boolean;
  /**
   * 토글 버튼에 표시할 강조 뱃지(예: 새로 오픈한 회차 "UPDATE"). comingSoon 이 우선한다.
   * 다음 회차가 오픈되면 이전 회차의 badge 는 제거한다.
   */
  badge?: "NEW" | "UPDATE";
  /**
   * 이 회차 파이널 모의고사의 출제 분포(subject별 count). 합은 20이며, 회차별 실제
   * 파이널 풀(_final_{id}_) 문제 수와 일치해야 부족 출제가 없다. 회차 목록과 같은 곳에 두어
   * 새 회차 추가 시 분포 누락(→ 조용한 오출제)을 막는다. comingSoon 회차는 생략 가능.
   */
  scopes?: FinalSessionScope[];
  /**
   * 사용자 노출 문구(RedAlert 등)용 이론/코딩 문제 수. 합은 scopes 와 같은 20이다.
   *
   * scopes 에서 파생하지 않고 회차마다 명시한다 — sql 을 26-1 은 이론(DB 세 번째 문제를
   * sql_final_1 로 보충, JCG-2076)으로, 26-2 는 코딩으로 분류해 회차별로 기준이 다르기
   * 때문이다. 자동 파생하면 26-1 이 코딩 10/이론 10 으로 잘못 표시된다.
   */
  composition?: { theory: number; coding: number };
}

/** 오래된 → 최신 순. 마지막 항목이 가장 최신 회차. 새 회차 추가 시 배열 끝에 append(scopes 동반). */
export const FINAL_SESSIONS: FinalSession[] = [
  {
    id: "2026-1",
    label: "26년 1회 대비",
    // 코딩 9(C4·Java4·Python1) + 이론 11 (JCG-1675). 세 번째 DB는 sql_final_1로 보충 (JCG-2076).
    composition: { theory: 11, coding: 9 },
    scopes: [
      { scope: "c", count: 4 },
      { scope: "java", count: 4 },
      { scope: "python", count: 1 },
      { scope: "db", count: 2 },
      { scope: "sql", count: 1 },
      { scope: "network-os", count: 2 },
      { scope: "sw-dev", count: 2 },
      { scope: "sw-design", count: 2 },
      { scope: "security-newtech", count: 2 },
    ],
  },
  {
    id: "2026-2",
    label: "26년 2회 대비",
    // 코딩 10(Python3·SQL3·C2·Java2) + 이론 10(보안3·SW설계2·네트워크OS2·DB2·SW개발1) (JCG-2913).
    composition: { theory: 10, coding: 10 },
    scopes: [
      { scope: "python", count: 3 },
      { scope: "sql", count: 3 },
      { scope: "c", count: 2 },
      { scope: "java", count: 2 },
      { scope: "security-newtech", count: 3 },
      { scope: "sw-design", count: 2 },
      { scope: "network-os", count: 2 },
      { scope: "db", count: 2 },
      { scope: "sw-dev", count: 1 },
    ],
  },
  {
    id: "2026-3",
    label: "26년 3회 대비",
    badge: "UPDATE",
    composition: { theory: 9, coding: 11 },
    scopes: [
      { scope: "c", count: 3 },
      { scope: "java", count: 3 },
      { scope: "python", count: 3 },
      { scope: "sql", count: 2 },
      { scope: "db", count: 1 },
      { scope: "network-os", count: 2 },
      { scope: "sw-dev", count: 2 },
      { scope: "sw-design", count: 1 },
      { scope: "security-newtech", count: 3 },
    ],
  },
];

const COUNTS = finalSessionCounts as Record<string, number>;

/** 회차별 파이널 문제 수(없으면 0). */
export function finalSessionCount(sessionId: string): number {
  return COUNTS[sessionId] ?? 0;
}

/**
 * 문제가 1개 이상 있어 실제 출제 가능한 회차인지.
 * comingSoon 회차는 문제 수와 무관하게 아직 출제 불가(준비 중)로 취급한다.
 */
export function isFinalSessionAvailable(sessionId: string): boolean {
  const session = FINAL_SESSIONS.find((s) => s.id === sessionId);
  if (session?.comingSoon) return false;
  return finalSessionCount(sessionId) > 0;
}

/**
 * 기본 노출 회차 — 문제가 있는 가장 최신 회차(FINAL_SESSIONS 순서상 마지막 available).
 * 전부 비어 있으면 가장 최신 회차(배열 마지막)를 폴백으로 반환한다.
 */
export function defaultFinalSessionId(): string {
  for (let i = FINAL_SESSIONS.length - 1; i >= 0; i--) {
    if (isFinalSessionAvailable(FINAL_SESSIONS[i].id)) return FINAL_SESSIONS[i].id;
  }
  return FINAL_SESSIONS[FINAL_SESSIONS.length - 1].id;
}

/**
 * 회차별 이론/코딩 문제 수(사용자 노출 문구용). 아직 구성이 정해지지 않은
 * comingSoon 회차는 null 이므로, 호출부는 문제 수 없이 안내해야 한다.
 */
export function finalSessionComposition(
  sessionId: string
): { theory: number; coding: number } | null {
  return FINAL_SESSIONS.find((s) => s.id === sessionId)?.composition ?? null;
}

/**
 * 회차별 파이널 출제 분포. 분포가 정의돼 있으면 그대로, 없으면 가장 먼저 정의된 회차(=26-1)
 * 분포로 폴백한다(안전장치). comingSoon 회차는 출제되지 않으므로 실제로는 호출되지 않는다.
 */
export function finalSessionScopes(sessionId: string): FinalSessionScope[] {
  const session = FINAL_SESSIONS.find((s) => s.id === sessionId);
  return session?.scopes ?? FINAL_SESSIONS.find((s) => s.scopes)?.scopes ?? [];
}
